import React from 'react';
import { Alert, Typography } from '@mui/material';
import { Controller } from 'react-hook-form';
import CheckboxCell from 'components/EqualisationElements/CheckboxCell';
import useAddCommitmentsToEqualisations from '../../../hooks/useAddCommitmentsToEqualisations';
import { round, sumBy } from 'lodash';
import { numberToCurrencyString, pluralize } from 'further-ui/utils';
import { useLayoutContext } from 'contexts/LayoutContext';
import { CapitalContributionRule } from 'further-types/lp/vehicle';
import PreviouslyClosedCommitmentsTable from './PreviouslyClosedCommitmentsTable';
import api from 'lib/trpcClient';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import NumberInput from 'further-ui/components/NumberInput';
import { Column } from 'further-ui/components/Layout';

const CommitmentsTable: React.FC = () => {
  const { vehicle } = useLayoutContext();
  const [equalisationsData] = api.equalisations.list.useSuspenseQuery({
    vehicleId: vehicle?._id ?? '',
  });

  const {
    control,
    formCommitments,
    previouslyClosedCommitments,
    pendingCloseCommitments,
    baseCurrency,
    handleSelectAll,
    handleToggleIsSelected,
    isAllSelected,
    isCapitalAndLoan,
    capitalCommitmentIsEditable,
    capitalCommitmentOnChange,
    loanCommitmentIsEditable,
    loanCommitmentOnChange,
    totalCommitmentIsEditable,
    totalCommitmentOnChange,
    capitalContributionRuleOverrideOnChange,
    showOverrideColumn,
    showOverrideCheckbox,
  } = useAddCommitmentsToEqualisations(vehicle);

  const sumOfSelectedCommitmentsCapitalCommitmentAmount = round(
    sumBy(
      formCommitments?.filter((c) => c.isSelected),
      'capitalCommitmentLcy',
    ) ?? 0,
    2,
  );

  const sumOfTotalCommitmentLcyForSelectedCommitments = round(
    sumBy(
      formCommitments?.filter(
        (c) => c.isSelected && !c.isCarryPartnerCommitment,
      ),
      'totalCommitmentLcy',
    ) ?? 0,
    2,
  );

  const onlyCarryPartnerIsSelected = formCommitments
    ?.filter((c) => c.isSelected)
    .every((c) => c.isCarryPartnerCommitment);

  const shouldShowFixedCapitalAmountWillIncreaseOnSubmitMessage =
    !!vehicle?.fixedCapitalAmount &&
    sumOfSelectedCommitmentsCapitalCommitmentAmount >
      vehicle?.fixedCapitalAmount;

  const shouldShowFixedCapitalAmountWillDecreaseOnSubmitMessage =
    !!vehicle?.fixedCapitalAmount &&
    sumOfTotalCommitmentLcyForSelectedCommitments <
      vehicle?.fixedCapitalAmount &&
    !onlyCarryPartnerIsSelected;

  const fixedCapitalAmountWillChangeOnSubmitMessage = `The capital contribution allocated above is higher than the fixed capital input on the Fund Setup page. If you submit this page, the vehicle's fixed capital will increase from ${numberToCurrencyString(
    vehicle?.fixedCapitalAmount,
    {
      currency: baseCurrency,
    },
  )} to ${numberToCurrencyString(
    sumOfSelectedCommitmentsCapitalCommitmentAmount,
    {
      currency: baseCurrency,
    },
  )}.`;

  const fixedCapitalAmountWillDecreaseOnSubmitMessage = `The total commitment amount being closed is lower than the fixed capital input on the Fund Setup page. If you submit this page, the vehicle's fixed capital will decrease from ${numberToCurrencyString(
    vehicle?.fixedCapitalAmount,
    {
      currency: baseCurrency,
    },
  )} to ${numberToCurrencyString(
    sumOfSelectedCommitmentsCapitalCommitmentAmount,
    {
      currency: baseCurrency,
    },
  )}.`;

  const shouldShowPreviouslyClosedCommitmentsTable =
    isCapitalAndLoan &&
    vehicle?.capitalContributionRule !==
      CapitalContributionRule.FixedCommitmentPercentage &&
    !!equalisationsData?.results?.length;

  type CommitmentRow = (typeof pendingCloseCommitments)[number];

  const getOriginalIndex = (row: CommitmentRow) =>
    formCommitments?.findIndex((c) => c.id === row.id) ?? -1;

  const columns: Array<TableColumn<CommitmentRow>> = [
    {
      key: 'investorName',
      label: 'Investor name',
      footer: (
        <>
          Total from {pendingCloseCommitments.length}{' '}
          {pluralize('investor', pendingCloseCommitments.length)}
        </>
      ),
      renderAvatar: (row) => ({ text: row.investorName, size: 'lg' }),
    },
    ...(!isCapitalAndLoan
      ? ([
          {
            key: 'totalCommitmentLcy',
            label: 'Total commitment',
            footer: (
              <>
                {numberToCurrencyString(
                  round(
                    sumBy(pendingCloseCommitments, 'totalCommitmentLcy'),
                    2,
                  ),
                  { currency: baseCurrency },
                )}
              </>
            ),
            render: (row) => {
              const originalIndex = getOriginalIndex(row);
              return (
                <Controller
                  control={control}
                  name={`commitments.${originalIndex}.totalCommitmentLcy`}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <NumberInput
                      size="small"
                      value={value ?? undefined}
                      onChange={onChange}
                      min={0}
                      decimals={2}
                      helperText={error?.message?.toString()}
                      error={!!error}
                      currencyStartAdornment
                      currency={baseCurrency}
                      fullWidth={false}
                    />
                  )}
                />
              );
            },
          },
        ] satisfies Array<TableColumn<CommitmentRow>>)
      : ([
          {
            key: 'capitalCommitmentLcy',
            label: 'Capital contribution',
            footer: (
              <>
                {numberToCurrencyString(
                  round(
                    sumBy(pendingCloseCommitments, 'capitalCommitmentLcy'),
                    2,
                  ),
                  { currency: baseCurrency },
                )}
              </>
            ),
            render: (row) => {
              const originalIndex = getOriginalIndex(row);
              return (
                <Controller
                  control={control}
                  name={`commitments.${originalIndex}.capitalCommitmentLcy`}
                  render={({ field: { value }, fieldState: { error } }) => (
                    <NumberInput
                      value={value ?? undefined}
                      onChange={(newValue: number | null) => {
                        if (newValue === undefined || newValue === null) return;
                        capitalCommitmentOnChange(
                          originalIndex,
                          newValue,
                          row.loanCommitmentLcy,
                        );
                      }}
                      min={0}
                      decimals={2}
                      helperText={error?.message?.toString()}
                      error={!!error}
                      currency={baseCurrency}
                      fullWidth={false}
                      currencyStartAdornment
                      disabled={
                        !capitalCommitmentIsEditable(
                          row.isCapitalContributionRuleOverride ?? false,
                          row.isCarryPartnerCommitment,
                        )
                      }
                    />
                  )}
                />
              );
            },
          },
          {
            key: 'loanCommitmentLcy',
            label: 'Loan commitment',
            footer: (
              <>
                {numberToCurrencyString(
                  round(sumBy(pendingCloseCommitments, 'loanCommitmentLcy'), 2),
                  { currency: baseCurrency },
                )}
              </>
            ),
            render: (row) => {
              const originalIndex = getOriginalIndex(row);
              return (
                <Controller
                  control={control}
                  name={`commitments.${originalIndex}.loanCommitmentLcy`}
                  render={({ field: { value }, fieldState: { error } }) => (
                    <NumberInput
                      value={value ?? undefined}
                      onChange={(newValue: number | null) => {
                        if (newValue === undefined || newValue === null) return;
                        loanCommitmentOnChange(
                          originalIndex,
                          newValue,
                          row.capitalCommitmentLcy,
                        );
                      }}
                      min={0}
                      decimals={2}
                      helperText={error?.message?.toString()}
                      error={!!error}
                      currency={baseCurrency}
                      currencyStartAdornment
                      fullWidth={false}
                      disabled={
                        !loanCommitmentIsEditable(
                          row.isCapitalContributionRuleOverride ?? false,
                          row.isCarryPartnerCommitment,
                        )
                      }
                    />
                  )}
                />
              );
            },
          },
          {
            key: 'totalCommitmentLcy',
            label: 'Total commitment',
            footer: (
              <>
                {numberToCurrencyString(
                  round(
                    sumBy(pendingCloseCommitments, 'totalCommitmentLcy'),
                    2,
                  ),
                  { currency: baseCurrency },
                )}
              </>
            ),
            render: (row) => {
              const originalIndex = getOriginalIndex(row);
              return (
                <Controller
                  control={control}
                  name={`commitments.${originalIndex}.totalCommitmentLcy`}
                  render={({ field: { value }, fieldState: { error } }) => (
                    <NumberInput
                      value={value ?? undefined}
                      onChange={(newValue: number | null) => {
                        if (newValue === undefined || newValue === null) return;
                        totalCommitmentOnChange(originalIndex, newValue);
                      }}
                      min={0}
                      decimals={2}
                      helperText={error?.message?.toString()}
                      error={!!error}
                      currency={baseCurrency}
                      currencyStartAdornment
                      fullWidth={false}
                      disabled={
                        !totalCommitmentIsEditable(
                          row.isCapitalContributionRuleOverride ?? false,
                          row.isCarryPartnerCommitment,
                        )
                      }
                    />
                  )}
                />
              );
            },
          },
          ...(showOverrideColumn
            ? ([
                {
                  key: 'isCapitalContributionRuleOverride',
                  label: 'Manual override',
                  render: (row) => {
                    if (!showOverrideCheckbox(row.isCarryPartnerCommitment))
                      return null;
                    const originalIndex = getOriginalIndex(row);
                    return (
                      <CheckboxCell
                        control={control}
                        name={`commitments.${originalIndex}.isCapitalContributionRuleOverride`}
                        onChange={(value) => {
                          capitalContributionRuleOverrideOnChange(
                            originalIndex,
                            value,
                            row.totalCommitmentLcy,
                            row.isCarryPartnerCommitment,
                          );
                        }}
                      />
                    );
                  },
                },
              ] satisfies Array<TableColumn<CommitmentRow>>)
            : []),
        ] satisfies Array<TableColumn<CommitmentRow>>)),
  ];

  return (
    <>
      {shouldShowPreviouslyClosedCommitmentsTable && (
        <Column spacing="xs">
          <Typography variant="h5">Previously closed commitments</Typography>
          <PreviouslyClosedCommitmentsTable
            previouslyClosedCommitments={previouslyClosedCommitments}
            baseCurrency={baseCurrency}
            capitalCommitmentIsEditable={capitalCommitmentIsEditable}
            capitalCommitmentOnChange={capitalCommitmentOnChange}
            loanCommitmentIsEditable={loanCommitmentIsEditable}
            loanCommitmentOnChange={loanCommitmentOnChange}
            capitalContributionRuleOverrideOnChange={
              capitalContributionRuleOverrideOnChange
            }
            showOverrideColumn={showOverrideColumn}
            showOverrideCheckbox={showOverrideCheckbox}
          />
        </Column>
      )}
      <Column spacing="xs">
        {shouldShowPreviouslyClosedCommitmentsTable ? (
          <Typography variant="h5">Select commitments</Typography>
        ) : null}
        <Table
          checkboxes={{
            isCheckedRow: (row) =>
              formCommitments?.find((c) => c.id === row.id)?.isSelected ??
              false,
            onRowCheckedToggle: (row) => {
              const originalIndex =
                formCommitments?.findIndex((c) => c.id === row.id) ?? -1;
              handleToggleIsSelected(originalIndex);
            },
            allRowsChecked: isAllSelected,
            onCheckAllToggle: handleSelectAll,
          }}
          rows={pendingCloseCommitments}
          columns={columns}
        />
      </Column>

      {(shouldShowFixedCapitalAmountWillIncreaseOnSubmitMessage ||
        shouldShowFixedCapitalAmountWillDecreaseOnSubmitMessage) && (
        <Alert severity="info">
          {shouldShowFixedCapitalAmountWillIncreaseOnSubmitMessage
            ? fixedCapitalAmountWillChangeOnSubmitMessage
            : fixedCapitalAmountWillDecreaseOnSubmitMessage}
        </Alert>
      )}
    </>
  );
};

export default CommitmentsTable;
